import { useFormik } from 'formik';
import IBAN from 'iban';
import React, { useState } from 'react';
import { FaPercent } from 'react-icons/fa6';
import { PiEnvelopeSimple } from 'react-icons/pi';
import { isValidPhoneNumber } from "react-phone-number-input";
import { Button, Modal, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../../components/forms/CustomSelect';
import RequiredInput from '../../../../components/forms/InputHOC';
import RequiredPassword from '../../../../components/forms/PasswordHOC';
import PhoneInput from '../../../../components/forms/PhoneInputWrapper';
import SelectTutorProvince from '../../../../components/forms/SelectTutorProvince';
import FormGroup from '../../../../layout/shared/form-group';
import { TutorService } from '../../../../services/tutor/tutorService';
import { Tutor } from '../../../../type/entities/tutor-type';
import { validateCifNif } from '../../../../utils/validatorFunctions';
import TutorProductsForm from '../../../tutors/components/TutorProductsForm';

interface TutorEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutor: Tutor;
  onTutorUpdate?: (tutorData: any) => void;
}

export const tutorType = [
  { value: 'internal', label: 'Interno (en nómina)' },
  { value: 'external', label: 'Externo (autónomo)' },
];

export const availability = [
  { value: 'morning', label: 'Mañanas' },
  { value: 'afternoon', label: 'Tardes' }
];

export const paymentMethods = [
  { value: 'hourly', label: 'Por horas' },
  { value: 'perStudent', label: 'Por alumno' },
];

const schema = yup.object({
  name: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  lastName: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  nifCif: yup.string().test('valid-cif-nif', 'El CIF/NIF no es válido', (value) => {
    if (value !== undefined && value.length > 0) {
      return validateCifNif(value);
    }
    return true;
  }).required('Campo obligatorio'),
  iban: yup.string().test('iban', 'El formato de IBAN no es válido', (value: any) => IBAN.isValid(value)),
  address: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  town: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  province: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  postalCode: yup.string().min(5, 'Demasiado corto').max(5, 'Demasiado largo').matches(/^[0-9]+$/, 'Código postal inválido').required('Campo obligatorio'),
  tutorType: yup.string().required('Campo Obligatorio').oneOf(['internal', 'external'], 'Debes seleccionar entre Interno o Externo'),
  email: yup.string().email('Correo electrónico inválido').required('Campo obligatorio'),
  telephone: yup.string().required('El teléfono es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
  telephoneSecondary: yup.string().nullable().test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value ?? '')),
  comments: yup.string().nullable(),
  withholding: yup.number().when('tutorType', {
    is: 'external',
    then: schema => schema.required('Campo obligatorio para tutor externo'),
    otherwise: schema => schema.notRequired().nullable(),
  })
    .min(0, 'El porcentaje no puede ser menor a 0%')
    .max(100, 'El porcentaje no puede ser mayor al 100%')
    .test(
      'max-2-decimals',
      'El número no puede tener más de 2 decimales',
      (value) => {
        if (value === undefined || value === null) return true;
        return /^\d+(\.\d{1,2})?$/.test(value.toString());
      }
    ),
});

const TutorEditModal: React.FC<TutorEditModalProps> = ({
  isOpen,
  onClose,
  tutor,
  onTutorUpdate,
}) => {

  const tutorService = new TutorService();

  const [isSaving, setIsSaving] = useState(false);

  const formik = useFormik({
    initialValues: {
      tutorId: tutor?.id ?? '',
      name: tutor?.name ?? '',
      lastName: tutor?.lastName ?? '',
      nifCif: tutor?.nifCif ?? '',
      iban: tutor?.iban ?? '',
      address: tutor?.address ?? '',
      town: tutor?.town ?? '',
      province: tutor?.province ?? '',
      postalCode: tutor?.postalCode ?? '',
      tutorType: tutor?.tutorType ?? '',
      email: tutor?.email ?? '',
      telephone: tutor?.telephone ?? '',
      telephoneSecondary: tutor?.telephoneSecondary ?? '',
      comments: tutor?.comments ?? '',
      availability: tutor?.availability ?? [],
      withholding: tutor?.withholding ?? 0,
      platformUser: tutor?.platformUser ?? '',
      platformPassword: tutor?.platformPassword ?? '',
      password: '',
      confirmPassword: '',
      active: tutor?.active ?? '',
    },
    validationSchema: schema,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsSaving(true);
        // Llamar al servicio para actualizar el tutor
        const response = await tutorService.editTutorProfile(values);

        if (response.getResponseData()) {
          if (onTutorUpdate) {
            onTutorUpdate(values);
          }
          toast.success('Perfil actualizado correctamente');
          handleClose();
        } else {
          toast.error('Error al actualizar el perfil');
        }
      } catch (error) {
        console.error('Error updating tutor:', error);
        toast.error('Error al actualizar el perfil');
      } finally {
        setIsSaving(false);
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full">
      <div className="m-auto px-7 pt-6 pb-8">
        <div className="mb-7 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Editar Tutor
          </h2>
        </div>

        <div className="@container">
          <form onSubmit={formik.handleSubmit}>
            <FormGroup
              title="Información General"
              description="Datos principales del tutor"
              className='pt-3 pb-4'
              titleCols="@md:col-span-2"
              childCols="@md:col-span-10 md:grid-cols-4"
            >
              <RequiredInput
                id='name'
                label="Nombre"
                formik={formik}
              />

              <RequiredInput
                id='lastName'
                label="Apellidos"
                formik={formik}
              />

              <RequiredInput
                id='nifCif'
                label="CIF/NIF"
                formik={formik}
              />

              <RequiredInput
                id='email'
                label="Email"
                formik={formik}
                prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
              />

              <RequiredInput
                id='iban'
                label="IBAN"
                formik={formik}
                placeholder="ES00 0000 0000 0000 0000 0000"
              />

              <PhoneInput
                id='telephone'
                label='Teléfono'
                onChange={e => { formik.setFieldValue('telephone', e) }}
                value={formik.values.telephone}
                error={formik.errors.telephone}
              />

              <PhoneInput
                id='telephoneSecondary'
                label='Teléfono Secundario'
                onChange={e => { formik.setFieldValue('telephoneSecondary', e) }}
                value={formik.values.telephoneSecondary}
                error={formik.errors.telephoneSecondary}
              />

              <CustomSelect
                id='tutorType'
                required
                label="Tipo de Tutor"
                options={tutorType}
                onChange={(e: any) => {
                  formik.setFieldValue('tutorType', e ? e.value : null);
                  formik.setFieldValue('products', []); // Reset products when changing tutor type to avoid validation errors
                  formik.setFieldValue('withholding', null); // Reset withholding when changing tutor type
                }}
                value={{ value: formik.values.tutorType, label: tutorType.find((g: any) => g.value === formik.values.tutorType)?.label }}
                error={formik.errors.tutorType}
              />

              <CustomSelect
                id='availability'
                isMulti
                required
                label="Disponibilidad"
                options={availability}
                onChange={(e: any) => formik.setFieldValue('availability', e ? e.map((g: any) => g.value) : [])}
                value={formik.values.availability.map((g: string) => availability.find((a: any) => a.value === g))}
                error={formik.errors.availability}
              />

              {formik.values.tutorType === 'external' && (
                <RequiredInput
                  id='withholding'
                  type="number"
                  label="Retenciones"
                  formik={formik}
                  suffix={<FaPercent />}
                />
              )}

              <RequiredInput
                id='platformUser'
                label="Usuario de la Plataforma"
                formik={formik}
              />

              <RequiredPassword
                id='platformPassword'
                label="Contraseña de la Plataforma"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                onChange={formik.handleChange}
                value={formik.values.platformPassword}
                error={formik.errors.platformPassword}
              />
            </FormGroup>

            <FormGroup
              title="Información Adicional"
              description="Datos de contacto y observaciones"
              className='pt-3 pb-4'
              titleCols="@md:col-span-2"
              childCols="@md:col-span-10 md:grid-cols-5"
            >
              <RequiredInput
                id='address'
                label="Dirección"
                formik={formik}
                className="md:col-span-2"
              />

              <RequiredInput
                id='town'
                label="Población"
                formik={formik}
              />

              <SelectTutorProvince
                required
                id='province'
                formik={formik}
              />

              <RequiredInput
                id='postalCode'
                label="Código Postal"
                formik={formik}
                placeholder="00000"
              />

              <div className="md:col-span-4">
                <Textarea
                  id='comments'
                  label="Comentarios"
                  rows={3}
                  className="[&>label>span]:font-medium"
                  placeholder="Observaciones adicionales..."
                  value={formik.values.comments || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.comments && formik.errors.comments ? formik.errors.comments : ''}
                />
              </div>

            </FormGroup>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                isLoading={isSaving}
                disabled={isSaving}
              >
                Guardar Cambios
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default TutorEditModal;
